import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Row } from '../../../../atoms/row/Row';
import { Typography } from '../../../../atoms/typography/Typography';
import { Checkbox } from '../../../../atoms/checkbox/Checkbox';
import { Stack } from '../../../../atoms/stack/Stack';
import { groupByProperty } from '../../../../../utils/utils';
import useAnswerChange from '../useAnswerChange';
import InfoPopup from '../../../shared/infoPopup/InfoPopup';

import SystemguideAnswerAccordion from '../../../../atoms/systemguide-answer-accordion/SystemguideAnswerAccordion';

const getCheckboxText = (value, t) => {
  switch (value) {
    case 'YES':
      return t('guide.answerYes');
    case 'NO':
      return t('guide.answerNo');
    case 'DONT_KNOW':
      return t('guide.answerDontKnow');
    case 'NOT_MATTER':
      return t('guide.answerNotMatter');
    case 'REQUIREMENT':
      return t('guide.requirement');
    default: {
      return value;
    }
  }
};

function fixImpact(impact) {
  switch (impact) {
    case 'low':
      return 'guide.impactLow';

    case 'medium':
      return 'guide.impactMedium';

    case 'high':
      return 'guide.impactHigh';

    default:
      return '';
  }
}

const MultipleQuestionImpact = ({ question, locale, t }) => {
  const { guideAnswers, id: questionId } = question;

  const groupedAnswers = groupByProperty(guideAnswers, 'section');
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const { control } = useFormContext();

  const { getCurrentAnswerObject, handleChangeMultiple } =
    useAnswerChange(questionId);

  const showImpact = question.answersHeadings?.includes('IMPACT');
  const showPercentage = question.answersHeadings?.includes('SHOW_PERCENTAGE');

  return (
    <Stack className="gap-12">
      <Controller
        control={control}
        name={`question-${questionId}`}
        render={({ field }) =>
          groupedAnswers.map((answers, index) => {
            const answersId = answers.id;

            return (
              <Stack key={answersId} className="min-w-fit">
                <Row className="justify-between">
                  <div>
                    {groupedAnswers.length > 0 && answers[0].section && (
                      <Typography as="span" fontVariant="md">
                        {t(
                          `guideQuestions.common.SYSTEM_CHARACTERISTICS.QUESTION_SECTIONS.${answers[0].section}.QUESTION`
                        )}
                      </Typography>
                    )}
                  </div>

                  {index === 0 && (showImpact || showPercentage) && (
                    <Row className="gap-1 items-start">
                      {showImpact && (
                        <Stack className="items-center gap-0">
                          <Typography
                            as="span"
                            fontVariant="xs"
                            className="w-20"
                          >
                            {t('guide.headingImpact')}
                          </Typography>
                        </Stack>
                      )}

                      {showPercentage && (
                        <Stack className="items-center gap-0">
                          <Typography
                            as="span"
                            fontVariant="xs"
                            className="w-20"
                          >
                            {t('catFilter.popularity')}
                          </Typography>

                          <InfoPopup
                            isOpen={isDescriptionOpen}
                            setIsOpen={setIsDescriptionOpen}
                            direction="right"
                            description="Nedan visar vad andra företag har valt med liknande profil som din (storlek, industri etc.)"
                          />
                        </Stack>
                      )}
                    </Row>
                  )}
                </Row>

                <Stack className="gap-3">
                  {answers
                    .sort((a, b) => a.order - b.order)
                    .map((answer) => {
                      const answerId = answer.id;
                      const currentAnswerObj = getCurrentAnswerObject(answerId);
                      const hasAnswerAlternatives =
                        question.answerAlternatives?.length > 0;

                      const rowClickable =
                        !hasAnswerAlternatives ||
                        question.answerAlternatives.length === 1;

                      return (
                        <div
                          key={answerId}
                          role="button"
                          tabIndex="0"
                          onKeyPress={() => {}}
                          onClick={() => {
                            if (rowClickable) {
                              const answerAlternative =
                                question.answerAlternatives?.[0] || null;

                              handleChangeMultiple(
                                answer,
                                answerAlternative,
                                question
                              );
                            }
                          }}
                          className={`flex flex-col sm:flex-row ${
                            (!hasAnswerAlternatives && '!flex-row',
                            rowClickable && 'cursor-pointer')
                          } w-full min-w-fit h-auto bg-[#EAF5FB] rounded-3xl p-4 pr-0 items-center justify-between gap-3 sm:gap-1`}
                        >
                          {/* <Typography
                            textAlign="left"
                            fontVariant="md"
                            className={`px-2 font-normal w-full min-w-44 sm:hidden ${
                              !hasAnswerAlternatives && 'hidden'
                            }`}
                          >
                            {answer.texts[locale]}
                          </Typography> */}

                          <div className="flex flex-row items-center justify-between w-full">
                            <div className="flex flex-col-reverse sm:flex-row flex-1 gap-2 items-start sm:items-center">
                              <div className="flex flex-col sm:flex-row gap-2">
                                {hasAnswerAlternatives ? (
                                  question.answerAlternatives.map(
                                    (answerAlternative) => (
                                      <Row
                                        onClick={() =>
                                          handleChangeMultiple(
                                            answer,
                                            answerAlternative,
                                            question,
                                            null
                                          )
                                        }
                                        className={`bg-primary-100  items-center p-2 ${
                                          answerAlternative !== 'TRUE' && 'pr-3'
                                        } h-9 rounded-lg gap-2 cursor-pointer`}
                                      >
                                        <Checkbox
                                          value={field.value}
                                          checked={
                                            currentAnswerObj?.text ===
                                            answerAlternative
                                          }
                                          onChange={() =>
                                            handleChangeMultiple(
                                              answer,
                                              answerAlternative,
                                              question,
                                              field.onChange
                                            )
                                          }
                                        />

                                        {answerAlternative !== 'TRUE' && (
                                          <Typography
                                            as="label"
                                            fontVariant="sm"
                                            className="cursor-pointer select-none text-nowrap"
                                          >
                                            {getCheckboxText(
                                              answerAlternative,
                                              t
                                            )}
                                          </Typography>
                                        )}
                                      </Row>
                                    )
                                  )
                                ) : (
                                  <Row
                                    onClick={() => handleChangeMultiple(answer)}
                                    className="bg-primary-100 items-center p-2 h-9 rounded-lg gap-2 cursor-pointer"
                                  >
                                    <Checkbox
                                      value={field.value}
                                      checked={
                                        currentAnswerObj?.answerId === answerId
                                      }
                                      onChange={() =>
                                        handleChangeMultiple(
                                          answer,
                                          null,
                                          field.onChange
                                        )
                                      }
                                    />
                                  </Row>
                                )}
                              </div>
                              <SystemguideAnswerAccordion
                                text={answer.texts[locale]}
                                textsHelp={answer.textsHelp[locale]}
                              />
                            </div>

                            {showImpact && (
                              <div className="flex min-w-20 max-sm:hidden h-full items-center border-l border-primary-100">
                                <Typography
                                  fontVariant="md"
                                  className="w-full h-fit !text-customGray-700"
                                >
                                  {t(fixImpact(answer.impact))}
                                </Typography>
                              </div>
                            )}

                            {showPercentage && (
                              <div className="flex min-w-20 max-sm:hidden h-full items-center border-l border-primary-100">
                                <Typography
                                  fontVariant="md"
                                  className="w-full h-fit !text-customGray-700"
                                >{`${answer.percentage || 0}%`}</Typography>
                              </div>
                            )}
                            <Row className="sm:hidden h-full">
                              {showImpact && answer?.impact?.length > 0 && (
                                <div className="flex min-w-20 h-full items-center border-l border-primary-100">
                                  <Typography
                                    fontVariant="md"
                                    className="w-full h-fit !text-customGray-700"
                                  >
                                    {t(fixImpact(answer.impact))}
                                  </Typography>
                                </div>
                              )}

                              {showPercentage && (
                                <div className="flex min-w-20 h-full items-center border-l border-primary-100">
                                  <Typography
                                    fontVariant="md"
                                    className="w-full h-fit !text-customGray-700"
                                  >{`${answer.percentage || 0}%`}</Typography>
                                </div>
                              )}
                            </Row>
                          </div>
                        </div>
                      );
                    })}
                </Stack>
              </Stack>
            );
          })
        }
      />
    </Stack>
  );
};

export default MultipleQuestionImpact;
